import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { AssetViewTabs, SelectSpaceDropdown } from '../../molecules';
import { AssetTableView } from '../AssetTableView';
import { AssetChartView } from '../AssetChartView';
import { AssetSourceView } from '../AssetSourceView';
import { Alert, Icon, NewButton } from '../../../../../components';

import {
  questionShape,
  spaceShape,
  termTranslationShape,
  sourceShape
} from '../../../../../propsShape';

const AssetDataContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const AssetDataHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 16px;
  border-bottom: 1px solid var(--border-muted);
  margin-bottom: 16px;
`;

const FailedRefreshAlertContainer = styled.div`
  padding: 0 16px;
  margin-bottom: 30px;
`;

const AssetData = ({
  question,
  termTranslations,
  navigate,
  handleCreateQuestion,
  onColumnOrderChange,
  activeView = 'table',
  handleViewChange,
  isChildQuestion = false,
  className = null,
  isReadOnly = true,
  sources = null,
  spaces = null,
  handleCollapseAsset = null,
  handleCreateAnswer = null,
  handleDeleteQuestion = null,
  handleRetryQuestion = null,
  onCreateSpace = null,
  onAssetTitleChange = null,
  onAddQuestionToSpace = null,
  onRemoveQuestionFromSpace = null,
  isAssetModalFailedRefreshAttempt = false,
  isEditSqlError,
  setIsEditSqlError
}) => {
  const answerSources = useMemo(
    () =>
      sources?.filter((source) => {
        if (question?.answer?.schemas) {
          if (
            question.answer.schemas
              .filter((s) => s !== 'multi' && s !== 'metrics')
              .includes(source.integration.schema)
          ) {
            return true;
          }
        }
        return false;
      }),
    [question, sources]
  );

  return (
    <AssetDataContainer className={className}>
      <AssetDataHeader>
        <AssetViewTabs
          handleCollapseAsset={handleCollapseAsset}
          activeView={activeView}
          answerSources={answerSources}
          chartTitle={
            question.lastChartFollowUp?.answer?.vegaSpec?.title?.text ||
            question.lastChartFollowUp?.answer?.vegaSpec?.title ||
            (question.lastChartFollowUp && 'Chart')
          }
          onViewChange={handleViewChange}
          showChartTab={!(question.answer?.error || question.answer?.isCanned) && !isChildQuestion}
          showSourceTab={question.answer?.rawSql}
        />
      </AssetDataHeader>

      {isAssetModalFailedRefreshAttempt && (
        <FailedRefreshAlertContainer>
          <Alert
            icon={<Icon color='var(--content-body)' name='alert' />}
            message='Failed to refresh asset'
            actions={
              <NewButton tertiary onClick={() => handleRetryQuestion(question.id)}>
                Try again
              </NewButton>
            }
          />
        </FailedRefreshAlertContainer>
      )}

      {!isAssetModalFailedRefreshAttempt && (
        <>
          {activeView === 'table' && (
            <AssetTableView
              isReadOnly={isReadOnly}
              question={question}
              termTranslations={termTranslations}
              spaces={spaces}
              answerSources={answerSources}
              navigate={navigate}
              onColumnOrderChange={onColumnOrderChange}
              onCreateQuestion={handleCreateQuestion}
              onAddQuestionToSpace={onAddQuestionToSpace}
              onDeleteQuestion={handleDeleteQuestion}
              onRetryQuestion={handleRetryQuestion}
              onRemoveQuestionFromSpace={onRemoveQuestionFromSpace}
              onCreateSpace={onCreateSpace}
              onAssetTitleChange={onAssetTitleChange}
            />
          )}
          {activeView === 'source' && (
            <AssetSourceView
              question={question}
              handleCreateAnswer={handleCreateAnswer}
              isEditSqlError={isEditSqlError}
              setIsEditSqlError={setIsEditSqlError}
            />
          )}
          {activeView === 'chart' && (
            <AssetChartView
              selectSpaceDropdown={
                !isReadOnly && (
                  <SelectSpaceDropdown
                    spaces={spaces}
                    defaultSelectedSpaceIds={question.spaceIds?.filter((id) =>
                      spaces?.some((space) => space.id === id)
                    )}
                    onAddQuestionToSpace={(space) => onAddQuestionToSpace(question, space)}
                    onRemoveQuestionFromSpace={(space) =>
                      onRemoveQuestionFromSpace(question, space)
                    }
                    onCreateSpace={onCreateSpace}
                  />
                )
              }
              question={question}
              onDeleteQuestion={handleDeleteQuestion}
              onRetryQuestion={handleRetryQuestion}
              answerSources={answerSources}
              emptyComponent={
                <AssetTableView
                  isReadOnly={isReadOnly}
                  question={question}
                  termTranslations={termTranslations}
                  answerSources={answerSources}
                  spaces={spaces}
                  onColumnOrderChange={onColumnOrderChange}
                  onAddQuestionToSpace={onAddQuestionToSpace}
                  onRemoveQuestionFromSpace={onRemoveQuestionFromSpace}
                  onCreateSpace={onCreateSpace}
                  onAssetTitleChange={onAssetTitleChange}
                />
              }
            />
          )}
        </>
      )}
    </AssetDataContainer>
  );
};

AssetData.propTypes = {
  className: PropTypes.string,
  isReadOnly: PropTypes.bool,
  question: questionShape.isRequired,
  spaces: PropTypes.arrayOf(spaceShape),
  termTranslations: PropTypes.arrayOf(termTranslationShape).isRequired,
  sources: PropTypes.arrayOf(sourceShape),
  navigate: PropTypes.func.isRequired,
  onColumnOrderChange: PropTypes.func.isRequired,
  handleViewChange: PropTypes.func,
  onAddQuestionToSpace: PropTypes.func,
  onRemoveQuestionFromSpace: PropTypes.func,
  onCreateSpace: PropTypes.func,
  onAssetTitleChange: PropTypes.func,
  handleDeleteQuestion: PropTypes.func,
  handleRetryQuestion: PropTypes.func,
  handleCreateQuestion: PropTypes.func.isRequired,
  handleCreateAnswer: PropTypes.func,
  handleCollapseAsset: PropTypes.func,
  isChildQuestion: PropTypes.bool,
  activeView: PropTypes.oneOf(['table', 'source', 'chart']),
  isAssetModalFailedRefreshAttempt: PropTypes.bool,
  isEditSqlError: PropTypes.bool.isRequired,
  setIsEditSqlError: PropTypes.func.isRequired
};

export { AssetData };
